/**
 * useAiPlanChat — hook for managing AI plan generation conversations.
 *
 * Handles:
 * - Sending messages to the AI backend
 * - Receiving plan proposals
 * - Tracking conversation history
 * - Executing accepted plans via executePlan()
 * - Saving proposals as reusable Skills
 */

import { useState, useCallback, useRef } from "react"
import type {
  AiChatMessage,
  AiPlanProposal,
  AiPlanStep,
  DbSkill,
  DbPack,
} from "@totodo/operations"
import { executePlan, addSkill, addPack } from "@/db/operations"
import i18next from "i18next"
import { apiRequest } from "@/shared/utils/api/apiClient"
import { aiRefusalMessage } from "@/features/ai-chat/aiQuotaDisplay"

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface ChatState {
  messages: AiChatMessage[]
  proposal: AiPlanProposal | null
  isGenerating: boolean
  error: string | null
}

interface GenerateResponse {
  ok: boolean
  proposal?: AiPlanProposal
  error?: string
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function useAiPlanChat() {
  const [state, setState] = useState<ChatState>({
    messages: [],
    proposal: null,
    isGenerating: false,
    error: null,
  })

  const abortRef = useRef<AbortController | null>(null)

  // Send a message and get AI response
  const sendMessage = useCallback(
    async (content: string, skill?: DbSkill) => {
      const userMessage: AiChatMessage = {
        role: "user",
        content,
        timestamp: new Date().toISOString(),
      }

      setState((prev) => ({
        ...prev,
        messages: [...prev.messages, userMessage],
        isGenerating: true,
        error: null,
      }))

      try {
        // Build messages for API (only user/assistant, no system)
        const apiMessages = [...state.messages, userMessage]
          .filter((m) => m.role === "user" || m.role === "assistant")
          .map((m) => ({ role: m.role, content: m.content }))

        const res = await apiRequest<GenerateResponse>({
          path: "/ai/generate-plan",
          method: "POST",
          timeout: 60_000,
          body: {
            messages: apiMessages,
            skill: skill
              ? {
                  name: skill.name,
                  description: skill.description,
                  systemPrompt: skill.systemPrompt,
                  constraints: skill.constraints,
                  exampleSteps: skill.exampleSteps,
                }
              : undefined,
          },
        })

        if (res.ok && res.data?.ok && res.data.proposal) {
          const proposal = res.data.proposal
          const assistantMessage: AiChatMessage = {
            role: "assistant",
            content: proposal.reasoning ?? `Here's a plan with ${proposal.steps.length} steps.`,
            proposal,
            timestamp: new Date().toISOString(),
          }

          setState((prev) => ({
            ...prev,
            messages: [...prev.messages, assistantMessage],
            proposal,
            isGenerating: false,
          }))
        } else {
          // Quota reached / no AI on the plan / the quota cannot be checked: the server says which
          const refusal = aiRefusalMessage(res.status, res.data, i18next.t.bind(i18next))
          if (!refusal) console.error("[useAiPlanChat] plan request failed:", res.status, res.data?.error)
          const errorMsg = refusal ?? i18next.t("Something went wrong. Please try again.")
          setState((prev) => ({
            ...prev,
            isGenerating: false,
            error: errorMsg,
          }))
        }
      } catch (e) {
        console.error("[useAiPlanChat] plan request failed:", e)
        setState((prev) => ({
          ...prev,
          isGenerating: false,
          error: i18next.t("Something went wrong. Please try again."),
        }))
      }
    },
    [state.messages],
  )

  // Execute the current proposal as a plan
  const acceptPlan = useCallback(
    async (anchorTaskId: string, anchorDate: number) => {
      if (!state.proposal) return { ok: false as const, error: "No proposal" }

      // Convert AiPlanProposal → DbPack (ephemeral, for executePlan)
      const ephemeralPack: DbPack = {
        id: `ai:${crypto.randomUUID()}`,
        kind: "pack",
        name: state.proposal.name,
        description: state.proposal.reasoning,
        category: "ai-generated",
        anchorDateType: state.proposal.anchorDateType,
        version: 1,
        steps: state.proposal.steps.map((s: AiPlanStep) => ({
          stepKey: `step_${s.sequenceOrder}`,
          titleTemplate: s.title,
          noteTemplate: s.note,
          offsetRule: s.offsetRule,
          sequenceOrder: s.sequenceOrder,
          tags: s.tags,
          checklistItems: s.checklistItems,
          estimatedMinutes: s.estimatedMinutes,
          reminderOffset: s.reminderOffset,
        })),
      }

      const result = await executePlan({
        anchorTaskId,
        anchorDate,
        anchorDateType: state.proposal.anchorDateType,
        pack: ephemeralPack,
      })

      return result
    },
    [state.proposal],
  )

  // Save proposal as a reusable Skill
  const saveAsSkill = useCallback(
    async (name: string, category: string) => {
      if (!state.proposal) return { ok: false as const, error: "No proposal" }

      // Build skill from conversation + proposal
      const systemPrompt = state.messages
        .filter((m) => m.role === "user")
        .map((m) => m.content)
        .join("\n")

      const result = await addSkill({
        name,
        category,
        systemPrompt: `Generate a plan similar to this conversation context:\n${systemPrompt}`,
        constraints: {
          minSteps: Math.max(1, state.proposal.steps.length - 2),
          maxSteps: state.proposal.steps.length + 3,
          anchorDateType: state.proposal.anchorDateType,
        },
        exampleSteps: state.proposal.steps.map((s) => ({
          title: s.title,
          typicalOffset: s.offsetRule,
          category: s.category,
          estimatedMinutes: s.estimatedMinutes,
        })),
        description: state.proposal.reasoning,
      })

      return result
    },
    [state.proposal, state.messages],
  )

  // Save proposal as a Pack (template with hardcoded steps)
  const saveAsPack = useCallback(
    async (name: string, category: string) => {
      if (!state.proposal) return { ok: false as const, error: "No proposal" }

      const result = await addPack({
        name,
        category,
        anchorDateType: state.proposal.anchorDateType,
        steps: state.proposal.steps.map((s) => ({
          stepKey: `step_${s.sequenceOrder}`,
          titleTemplate: s.title,
          noteTemplate: s.note,
          offsetRule: s.offsetRule,
          sequenceOrder: s.sequenceOrder,
          tags: s.tags,
          checklistItems: s.checklistItems,
          estimatedMinutes: s.estimatedMinutes,
          reminderOffset: s.reminderOffset,
        })),
        description: state.proposal.reasoning,
      })

      return result
    },
    [state.proposal],
  )

  // Reset conversation
  const reset = useCallback(() => {
    abortRef.current?.abort()
    setState({
      messages: [],
      proposal: null,
      isGenerating: false,
      error: null,
    })
  }, [])

  return {
    ...state,
    sendMessage,
    acceptPlan,
    saveAsSkill,
    saveAsPack,
    reset,
  }
}
